import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Search, Calendar as CalendarIcon, Users, ChevronLeft, ChevronRight, Check, X, UserCheck, Shield, Star, Coins, Pencil, Lock, Save, FileText, Clock } from "lucide-react";
import { offlinePost } from "@/lib/offlineApi";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const CLASS_TYPES = [
  { value: "Tigers Kung Fu", label: "Tigers Kung Fu" },
  { value: "Dragons Kung Fu", label: "Dragons Kung Fu" },
  { value: "Adults Kung Fu", label: "Adults Kung Fu" },
  { value: "Tai Chi", label: "Tai Chi" },
];

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const ITEMS_PER_PAGE = 25;

const getInsuranceStatus = (student) => {
  if (!student.membership_paid_date) {
    return { status: "none", color: "bg-gray-500", dotColor: "bg-gray-400" };
  }
  const paidDate = new Date(student.membership_paid_date);
  const expiryDate = new Date(paidDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  if (daysUntilExpiry < 0) return { status: "expired", color: "bg-red-500", dotColor: "bg-red-500", label: `Expired ${Math.abs(daysUntilExpiry)}d ago` };
  if (daysUntilExpiry <= 30) return { status: "expiring", color: "bg-orange-500", dotColor: "bg-orange-500", label: `Expires in ${daysUntilExpiry}d` };
  return { status: "valid", color: "bg-green-500", dotColor: "bg-green-500", label: `Valid (${daysUntilExpiry}d)` };
};

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const parts = dateOfBirth.split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  const birthDate = new Date(year, month, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const getBeltColor = (belt) => {
  const colors = {
    "White": "bg-gray-100 text-black", "Yellow": "bg-yellow-400 text-black",
    "Orange": "bg-orange-400 text-black", "Green": "bg-green-400 text-black",
    "Blue": "bg-blue-400 text-black", "Purple": "bg-purple-400 text-black",
    "Brown": "bg-amber-700 text-white", "Red": "bg-red-600 text-white",
    "Red 1 stripe": "bg-red-600 text-white", "Red 2 stripes": "bg-red-600 text-white",
    "Red 3 Stripes": "bg-red-600 text-white", "Black": "bg-gray-900 text-white border border-white/20",
  };
  return colors[belt] || "bg-gray-500 text-white";
};

const getAdultPillarColor = (pillar) => {
  if (!pillar) return "bg-gray-500";
  if (pillar.includes("Chou Kup")) return "bg-blue-600";
  if (pillar.includes("Jung Kup")) return "bg-purple-600";
  if (pillar.includes("Gou Kup")) return "bg-orange-600";
  if (pillar.includes("Gau Lin")) return "bg-red-600";
  return "bg-gray-500";
};

const getClassColor = (classType) => {
  if (classType?.includes("Tigers")) return "bg-orange-500/20 text-orange-400";
  if (classType?.includes("Dragons")) return "bg-blue-500/20 text-blue-400";
  if (classType?.includes("Tai Chi")) return "bg-teal-500/20 text-teal-400";
  return "bg-red-500/20 text-red-400";
};

const isAdultClass = (classType) => classType === "Adults Kung Fu";

const getAttendanceColor = (percentage) => {
  if (percentage >= 80) return "text-green-400";
  if (percentage >= 60) return "text-yellow-400";
  if (percentage >= 40) return "text-orange-400";
  return "text-red-400";
};

// Format date to DD/MM/YYYY
const formatDateStr = (d) => {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// Parse DD/MM/YYYY to Date
const parseDateStr = (str) => {
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
};

// Convert DD/MM/YYYY to YYYY-MM-DD for input[type=date]
const toInputDate = (str) => {
  const parts = str.split('/');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
};

// Convert YYYY-MM-DD to DD/MM/YYYY
const fromInputDate = (str) => {
  if (!str) return '';
  const parts = str.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Attendance() {
  const [students, setStudents] = useState([]);
  const [dateAttendance, setDateAttendance] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState("all");
  const [processing, setProcessing] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [todaysClasses, setTodaysClasses] = useState([]);
  const [todaysSchedule, setTodaysSchedule] = useState([]);

  // v7.1: Date navigation
  const today = new Date();
  const todayStr = formatDateStr(today);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const isToday = selectedDate === todayStr;
  const [isHistoricalEdit, setIsHistoricalEdit] = useState(false);

  // v7.1: Class Notes
  const [classNotes, setClassNotes] = useState({});
  const [editingNotes, setEditingNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState(false);

  const monthName = today.toLocaleString('default', { month: 'long' });
  const todayDayName = DAY_NAMES[today.getDay()];
  const currentHour = today.getHours();
  const selectedDateObj = parseDateStr(selectedDate);
  const selectedDayName = selectedDateObj ? DAY_NAMES[selectedDateObj.getDay()] : '';

  useEffect(() => { fetchData(); }, [selectedDate]);
  useEffect(() => { setCurrentPage(1); }, [search, filterClass]);

  const getAutoSelectClass = (schedule) => {
    if (schedule.length === 0) return null;
    const sorted = [...schedule].sort((a, b) => (a.start_time || "18:00").localeCompare(b.start_time || "18:00"));
    for (let i = sorted.length - 1; i >= 0; i--) {
      const classHour = parseInt((sorted[i].start_time || "18:00").split(":")[0], 10);
      if (currentHour >= classHour - 1) return sorted[i].class_type;
    }
    return sorted[0].class_type;
  };

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, attendanceRes, reportRes, scheduleRes, notesRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/attendance?date=${selectedDate}`),
        axios.get(`${API}/attendance/report?period=monthly`),
        axios.get(`${API}/class-schedule`).catch(() => ({ data: [] })),
        axios.get(`${API}/class-notes?date=${selectedDate}`).catch(() => ({ data: [] })),
      ]);
      setStudents(studentsRes.data);
      setDateAttendance(attendanceRes.data);

      const statsMap = {};
      reportRes.data.students?.forEach(s => { statsMap[s.student_id] = s.attendance_percentage; });
      setMonthlyStats(statsMap);

      // Schedule for the selected date's day
      const dayName = selectedDateObj ? DAY_NAMES[selectedDateObj.getDay()] : todayDayName;
      const schedulesForDay = scheduleRes.data.filter(s => s.day_of_week === dayName);
      setTodaysSchedule(schedulesForDay);
      const classesForDay = schedulesForDay.map(s => s.class_type);
      setTodaysClasses(classesForDay);

      if (filterClass === "all" && classesForDay.length > 0 && isToday) {
        const autoClass = getAutoSelectClass(schedulesForDay);
        if (autoClass) setFilterClass(autoClass);
      }

      // Load class notes
      const notesMap = {};
      (notesRes.data || []).forEach(n => { notesMap[n.class_type] = n.notes; });
      setClassNotes(notesMap);
      setEditingNotes(notesMap);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setIsHistoricalEdit(false);
    }
  }, [selectedDate]);

  // v7.3: Lightweight attendance-only refresh (no full page refetch)
  const refreshAttendanceOnly = async () => {
    try {
      const res = await axios.get(`${API}/attendance?date=${selectedDate}`);
      setDateAttendance(res.data);
    } catch { /* silent */ }
  };

  // v7.3: Optimistic mark present — local state update, no page jump
  // v12.3.0: Offline-aware — queues if offline
  const handleMarkPresent = async (student) => {
    if (processing || isPresent(student.id)) return;
    setProcessing(student.id);
    try {
      const res = await offlinePost('/attendance', { student_id: student.id, date: selectedDate });
      if (res.data.offline) {
        // Optimistic local update for offline
        setDateAttendance(prev => [...prev, { id: `offline-${Date.now()}`, student_id: student.id, date: selectedDate }]);
        toast.info(`${student.name} marked Present (offline — will sync)`);
      } else {
        setDateAttendance(prev => [...prev, res.data]);
        toast.success(`${student.name} marked Present`);
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes("already checked in")) {
        toast.info(`${student.name} is already marked present`);
      } else {
        toast.error("Failed to mark present");
      }
    } finally { setProcessing(null); }
  };

  // v7.3: Optimistic mark absent — local state removal, no page jump
  const handleMarkAbsent = async (student) => {
    if (processing) return;
    const record = getAttendanceRecord(student.id);
    if (!record) return;
    setProcessing(student.id);
    try {
      await axios.delete(`${API}/attendance/${record.id}`);
      // Optimistic local update — remove the record without refetching
      setDateAttendance(prev => prev.filter(a => a.id !== record.id));
      toast.success(`${student.name} marked Absent`);
    } catch { toast.error("Failed to mark absent"); }
    finally { setProcessing(null); }
  };

  // v7.3: Bulk check-in — lightweight refresh (attendance only)
  const handleBulkCheckIn = async () => {
    if (filterClass === "all") { toast.error("Select a class first"); return; }
    setBulkProcessing(true);
    try {
      const response = await axios.post(`${API}/attendance/bulk-checkin?class_type=${encodeURIComponent(filterClass)}&date=${selectedDate}`);
      toast.success(`Bulk check-in: ${response.data.checked_in} students marked present`);
      // Only refresh attendance records, not the entire page
      await refreshAttendanceOnly();
    } catch { toast.error("Bulk check-in failed"); }
    finally { setBulkProcessing(false); }
  };

  // Save class notes
  const handleSaveNotes = async (classType) => {
    setSavingNotes(true);
    try {
      await axios.post(`${API}/class-notes`, {
        date: selectedDate,
        class_type: classType,
        notes: editingNotes[classType] || "",
      });
      setClassNotes(prev => ({ ...prev, [classType]: editingNotes[classType] || "" }));
      toast.success("Notes saved");
    } catch { toast.error("Failed to save notes"); }
    finally { setSavingNotes(false); }
  };

  const isPresent = (studentId) => dateAttendance.some((a) => a.student_id === studentId);
  const getAttendanceRecord = (studentId) => dateAttendance.find((a) => a.student_id === studentId);

  const studentInClass = (student, classType) => {
    if (student.class_types && student.class_types.length > 0) return student.class_types.includes(classType);
    return student.class_type === classType;
  };

  const getStudentClassesDisplay = (student) => {
    if (student.class_types && student.class_types.length > 0) return student.class_types.map(c => c.replace(" Kung Fu", "")).join(", ");
    return student.class_type ? student.class_type.replace(" Kung Fu", "") : "";
  };

  const GROUP_CLASS_TYPES = ["Tigers Kung Fu", "Dragons Kung Fu", "Adults Kung Fu", "Tai Chi"];

  const filteredStudents = students.filter((student) => {
    const matchesSearch = student.name.toLowerCase().includes(search.toLowerCase());
    const matchesClass = filterClass === "all" || studentInClass(student, filterClass);
    const isGroupStudent = GROUP_CLASS_TYPES.some(gc => studentInClass(student, gc));
    return matchesSearch && matchesClass && isGroupStudent;
  });

  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const presentCount = dateAttendance.length;
  const totalStudents = students.length;
  const filteredPresentCount = filteredStudents.filter(s => isPresent(s.id)).length;

  // Daily snapshot: headcount by class
  const classCounts = {};
  dateAttendance.forEach(a => {
    const student = students.find(s => s.id === a.student_id);
    if (!student) return;
    const classes = student.class_types?.length ? student.class_types : (student.class_type ? [student.class_type] : []);
    classes.forEach(cls => {
      classCounts[cls] = (classCounts[cls] || 0) + 1;
    });
  });

  const currentClassName = filterClass === "all" ? "All Classes" : filterClass.replace(" Kung Fu", "");

  const getHeaderClassColor = () => {
    if (filterClass.includes("Tigers")) return "text-orange-400 border-orange-400";
    if (filterClass.includes("Dragons")) return "text-blue-400 border-blue-400";
    if (filterClass.includes("Adults")) return "text-red-400 border-red-400";
    if (filterClass.includes("Tai Chi")) return "text-teal-400 border-teal-400";
    return "text-[#D4AF37] border-[#D4AF37]";
  };

  // Can edit? Today always editable. Historical only if edit mode is on.
  const canEdit = isToday || isHistoricalEdit;

  // Date navigation
  const handleDateChange = (inputDate) => {
    const newDate = fromInputDate(inputDate);
    if (newDate) {
      setSelectedDate(newDate);
      setIsHistoricalEdit(false);
    }
  };

  const goToToday = () => {
    setSelectedDate(todayStr);
    setIsHistoricalEdit(false);
  };

  return (
    <div data-testid="attendance-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col gap-4">
        {filterClass !== "all" && (
          <div className={`text-center py-3 px-4 bg-[#18181b] border-2 rounded-sm ${getHeaderClassColor()}`}>
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="current-class-banner">
              {currentClassName} Register
            </h2>
            {todaysClasses.length > 0 && (
              <p className="text-sm text-[#A1A1AA] mt-1">
                Classes scheduled: {todaysClasses.map(c => c.replace(" Kung Fu", "")).join(" → ")}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Attendance Register 出席記錄
            </h1>
            {/* v7.1: Date picker — high contrast gold */}
            <div className="flex items-center gap-3 mt-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="attendance-date-picker"
                    variant="outline"
                    className="bg-[#27272a] border-[#D4AF37] hover:border-[#FFD700] text-[#FFD700] hover:bg-[#D4AF37]/15 px-4 py-2 h-auto gap-2 font-semibold text-base shadow-sm shadow-[#D4AF37]/20"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    <CalendarIcon className="w-5 h-5 text-[#FFD700]" />
                    <span className="text-white text-base tracking-wide font-bold">{selectedDate}</span>
                    {isToday && <span className="text-[10px] text-[#22c55e] ml-1 font-normal">(LIVE)</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#18181b] border-[#D4AF37]/30" align="start" style={{ transform: 'scale(1.2)', transformOrigin: 'top left' }}>
                  <Calendar
                    mode="single"
                    selected={parseDateStr(selectedDate)}
                    onSelect={(date) => {
                      if (date && date <= today) {
                        setSelectedDate(formatDateStr(date));
                        setIsHistoricalEdit(false);
                      }
                    }}
                    disabled={(date) => date > today}
                    modifiers={{
                      classDay: (date) => {
                        const day = date.getDay();
                        return (day === 1 || day === 3) && date <= today;
                      },
                    }}
                    modifiersClassNames={{
                      classDay: "ring-2 ring-[#D4AF37] ring-inset text-[#D4AF37] font-bold",
                    }}
                    className="bg-[#18181b] text-white rounded-sm"
                  />
                  <div className="px-3 pb-2 flex items-center gap-2 border-t border-white/5 pt-2">
                    <div className="w-3 h-3 rounded-sm ring-2 ring-[#D4AF37]" />
                    <span className="text-[#A1A1AA] text-[10px]">Mon & Wed (Class Days)</span>
                  </div>
                </PopoverContent>
              </Popover>

              {!isToday && (
                <div className="flex items-center gap-2">
                  <span className="text-[#D4AF37] text-sm font-semibold px-3 py-1.5 bg-[#D4AF37]/15 border-2 border-[#D4AF37]/40 rounded-sm flex items-center gap-1.5" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    <Clock className="w-4 h-4" />
                    TIME TRAVEL
                  </span>
                  <Button size="sm" onClick={goToToday} className="bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs h-8 font-semibold">
                    Back to Today
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 bg-[#18181b] border border-white/10 rounded-sm p-4">
            <div className="p-3 bg-[#27272a] rounded-sm">
              <Users className="w-6 h-6 text-[#22c55e]" />
            </div>
            <div>
              <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Present {isToday ? "Today" : selectedDate}</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="today-present-count">
                {presentCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* v7.1: Daily Snapshot */}
      {presentCount > 0 && (
        <Card data-testid="daily-snapshot" className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Daily Snapshot — {selectedDate}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-sm px-3 py-2">
                <p className="text-lg font-bold text-[#22c55e]" style={{ fontFamily: 'Oswald, sans-serif' }}>{presentCount}</p>
                <p className="text-[10px] text-[#A1A1AA] uppercase">Total Headcount</p>
              </div>
              {Object.entries(classCounts).map(([cls, count]) => (
                <div key={cls} className="bg-white/5 border border-white/10 rounded-sm px-3 py-2">
                  <p className="text-lg font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{count}</p>
                  <p className="text-[10px] text-[#A1A1AA] uppercase">{cls.replace(" Kung Fu", "")}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* v7.1: Class Notes - per class per date — HIGH CONTRAST */}
      {filterClass !== "all" && (
        <Card data-testid="class-notes-card" className="bg-[#1c1c20] border-[#D4AF37]/30 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="text-base font-semibold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Lesson Notes — {currentClassName}
                </h3>
                <span className="text-[10px] text-[#A1A1AA] ml-1">{selectedDate}</span>
              </div>
              <Button
                size="sm"
                onClick={() => handleSaveNotes(filterClass)}
                disabled={savingNotes || editingNotes[filterClass] === classNotes[filterClass]}
                className="bg-[#D4AF37] hover:bg-[#b8962e] text-black text-xs h-8 px-4 font-semibold disabled:opacity-40"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <Save className="w-3.5 h-3.5 mr-1" /> {savingNotes ? "Saving..." : "Save Notes"}
              </Button>
            </div>
            <Textarea
              data-testid="class-notes-input"
              value={editingNotes[filterClass] || ""}
              onChange={(e) => setEditingNotes(prev => ({ ...prev, [filterClass]: e.target.value }))}
              placeholder="What was taught today? e.g., Spear Form - Lán, Ná, Zhā"
              className="bg-[#27272a] border-white/20 text-white placeholder-[#71717a] min-h-[80px] text-base leading-relaxed focus:border-[#D4AF37]/50"
              style={{ fontSize: '15px' }}
            />
          </CardContent>
        </Card>
      )}

      {/* Historical Edit Controls — HIGH CONTRAST */}
      {!isToday && !isHistoricalEdit && (
        <Card className="bg-[#D4AF37]/10 border-2 border-[#D4AF37]/50 rounded-sm shadow-lg shadow-[#D4AF37]/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#D4AF37]/20 rounded-sm">
                <Lock className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Read-Only Historical Snapshot
                </p>
                <p className="text-white/70 text-xs mt-0.5">Viewing records for {selectedDate}. Enable edit to add/remove students.</p>
              </div>
            </div>
            <Button
              data-testid="enable-edit-btn"
              size="sm"
              onClick={() => setIsHistoricalEdit(true)}
              className="bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold text-xs h-8 px-4"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Enable Edit
            </Button>
          </CardContent>
        </Card>
      )}
      {!isToday && isHistoricalEdit && (
        <Card className="bg-[#22c55e]/10 border-2 border-[#22c55e]/50 rounded-sm shadow-lg shadow-[#22c55e]/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#22c55e]/20 rounded-sm">
                <Pencil className="w-5 h-5 text-[#22c55e]" />
              </div>
              <div>
                <p className="text-[#22c55e] font-semibold text-sm uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Editing Mode Active
                </p>
                <p className="text-white/70 text-xs mt-0.5">Changes will apply to {selectedDate}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setIsHistoricalEdit(false)} className="bg-white/10 hover:bg-white/20 text-white text-xs h-8">
              <Lock className="w-3 h-3 mr-1" /> Lock
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters & Bulk Actions */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="search-attendance-input" value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white" placeholder="Search students..." />
            </div>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger data-testid="filter-attendance-class" className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Filter by class" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Classes</SelectItem>
                {CLASS_TYPES.map((c) => {
                  const isScheduled = todaysClasses.includes(c.value);
                  return (
                    <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">
                      {c.label} {isScheduled && <span className="text-green-400 ml-1">(Scheduled)</span>}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {canEdit && (
              <Button onClick={handleBulkCheckIn} disabled={filterClass === "all" || bulkProcessing}
                className="bg-[#22c55e] hover:bg-[#16a34a] text-white disabled:opacity-50" data-testid="bulk-checkin-btn">
                <UserCheck className="w-4 h-4 mr-2" />
                {bulkProcessing ? "Processing..." : "Check-in All"}
              </Button>
            )}
          </div>

          {filterClass !== "all" && (
            <p className="text-[#A1A1AA] text-sm mt-2">
              {filterClass.replace(" Kung Fu", "")}: {filteredPresentCount}/{filteredStudents.length} present
            </p>
          )}
        </CardContent>
      </Card>

      {/* Students Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="text-[#A1A1AA]">Loading...</div></div>
      ) : filteredStudents.length === 0 ? (
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-8 text-center"><p className="text-[#A1A1AA]">No students found</p></CardContent>
        </Card>
      ) : (
        <>
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-16 text-center hidden md:table-cell">Age</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Rank</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-24 text-center hidden md:table-cell">{monthName} %</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-24 text-center">Present</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-24 text-center">Absent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedStudents.map((student) => {
                      const present = isPresent(student.id);
                      const isAdult = studentInClass(student, "Adults Kung Fu");
                      const age = calculateAge(student.date_of_birth);
                      const isProcessing = processing === student.id;
                      const monthlyPercentage = monthlyStats[student.id] || 0;

                      return (
                        <TableRow key={student.id}
                          className={`border-white/5 transition-colors ${present ? "bg-[#22c55e]/5" : "hover:bg-white/5"}`}
                          data-testid={`attendance-row-${student.id}`}>
                          {/* Name + billing icons */}
                          <TableCell className="font-medium text-white">
                            <div className="flex items-center gap-2">
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <div className={`w-2.5 h-2.5 rounded-full ${getInsuranceStatus(student).dotColor} flex-shrink-0`} />
                              </TooltipTrigger><TooltipContent className="bg-[#27272a] border-white/10">
                                <div className="flex items-center gap-2"><Shield className="w-3 h-3" /><span>Insurance: {getInsuranceStatus(student).label || "No data"}</span></div>
                              </TooltipContent></Tooltip></TooltipProvider>
                              <span>{student.name}</span>
                              {isAdult && student.is_sifu && (
                                <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-sm bg-[#D4AF37] text-black font-bold">師父</span>
                              )}
                              {/* v7.1: Billing type icons */}
                              {student.billing_type === "exempt" && (
                                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                  <Star className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37] flex-shrink-0" />
                                </TooltipTrigger><TooltipContent className="bg-[#27272a] border-[#D4AF37]/30"><p className="text-[#D4AF37] text-xs">Exempt</p></TooltipContent></Tooltip></TooltipProvider>
                              )}
                              {student.billing_type === "pay_as_you_go" && (
                                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                  <Coins className="w-3.5 h-3.5 text-[#14b8a6] flex-shrink-0" />
                                </TooltipTrigger><TooltipContent className="bg-[#27272a] border-teal-500/30"><p className="text-teal-400 text-xs">Pay-As-You-Go</p></TooltipContent></Tooltip></TooltipProvider>
                              )}
                            </div>
                          </TableCell>

                          <TableCell className="text-center text-[#A1A1AA] hidden md:table-cell">{age !== null ? age : "—"}</TableCell>

                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(student.class_types?.length ? student.class_types : [student.class_type]).filter(Boolean).map((classType, idx) => (
                                <span key={idx} className={`inline-flex px-2 py-0.5 text-xs rounded-sm ${getClassColor(classType)}`}>
                                  {classType.replace(" Kung Fu", "")}
                                </span>
                              ))}
                            </div>
                          </TableCell>

                          <TableCell>
                            {!isAdult ? (
                              <span className={`inline-flex px-2 py-0.5 text-xs rounded-sm ${getBeltColor(student.belt_rank)}`}>{student.belt_rank}</span>
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                {student.adult_pillar && (
                                  <span className={`inline-flex px-2 py-0.5 text-[10px] rounded-sm text-white ${getAdultPillarColor(student.adult_pillar)}`}>
                                    {student.adult_pillar.split(" – ")[0]}
                                  </span>
                                )}
                                {student.adult_level && <span className="text-[#A1A1AA] text-[10px]">L{student.adult_level.split(".")[0]}</span>}
                                {!student.adult_pillar && !student.adult_level && <span className="text-[#A1A1AA]">—</span>}
                              </div>
                            )}
                          </TableCell>

                          <TableCell className="text-center hidden md:table-cell">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-bold text-sm ${getAttendanceColor(monthlyPercentage)}`}>{monthlyPercentage}%</span>
                              <Progress value={monthlyPercentage} className="h-1.5 w-16 bg-white/10" />
                            </div>
                          </TableCell>

                          {/* Present/Absent buttons - disabled in read-only historical view */}
                          <TableCell className="text-center">
                            <Button
                              onClick={() => canEdit && handleMarkPresent(student)}
                              disabled={isProcessing || !canEdit}
                              className={`w-16 h-14 sm:w-16 sm:h-14 md:w-14 md:h-12 rounded-sm transition-all ${
                                present
                                  ? "bg-[#22c55e] hover:bg-[#22c55e] text-white shadow-lg shadow-green-500/30 scale-105"
                                  : canEdit
                                    ? "bg-[#27272a] hover:bg-[#22c55e]/20 text-[#A1A1AA] hover:text-[#22c55e] border border-white/10"
                                    : "bg-[#27272a] text-[#52525b] border border-white/5 cursor-not-allowed"
                              }`}
                              data-testid={`present-btn-${student.id}`}>
                              {isProcessing ? <span className="animate-pulse">...</span> : <Check className={`w-7 h-7 sm:w-7 sm:h-7 ${present ? "text-white" : ""}`} strokeWidth={3} />}
                            </Button>
                          </TableCell>

                          <TableCell className="text-center">
                            <Button
                              onClick={() => canEdit && handleMarkAbsent(student)}
                              disabled={isProcessing || !canEdit}
                              className={`w-16 h-14 sm:w-16 sm:h-14 md:w-14 md:h-12 rounded-sm transition-all ${
                                !present
                                  ? "bg-[#ef4444] hover:bg-[#ef4444] text-white shadow-lg shadow-red-500/30 scale-105"
                                  : canEdit
                                    ? "bg-[#27272a] hover:bg-[#ef4444]/20 text-[#A1A1AA] hover:text-[#ef4444] border border-white/10"
                                    : "bg-[#27272a] text-[#52525b] border border-white/5 cursor-not-allowed"
                              }`}
                              data-testid={`absent-btn-${student.id}`}>
                              {isProcessing ? <span className="animate-pulse">...</span> : <X className={`w-7 h-7 sm:w-7 sm:h-7 ${!present ? "text-white" : ""}`} strokeWidth={3} />}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-[#18181b] border border-white/10 rounded-sm">
              <p className="text-[#A1A1AA] text-sm">
                {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1} className="border-white/10 text-white hover:bg-white/5 disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) page = i + 1;
                    else if (currentPage <= 3) page = i + 1;
                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                    else page = currentPage - 2 + i;
                    return (
                      <Button key={page} variant={currentPage === page ? "default" : "outline"} size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? "bg-[#D4AF37] hover:bg-[#b8962e] text-black min-w-[32px]" : "border-white/10 text-white hover:bg-white/5 min-w-[32px]"}>
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages} className="border-white/10 text-white hover:bg-white/5 disabled:opacity-50">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
